import axios from 'axios';
import { config } from '../config';

const API_URL = config.API_BASE_URL;

export interface AiAgentSettingsDTO {
    id?: number;
    sectorId: number;
    isEnabled: boolean;
    prompt: string;
    knowledgeBase: string;
    autoTransferEnabled: boolean;
    humanTransferRulesEnabled: boolean;
    humanTransferRules: string;
    aiApiKey: string;
    aiModel: string;
    inactivityTimeoutMinutes?: number;
    respondWithAudio?: boolean;
    /** Legacy field — ElevenLabs UI removed. */
    elevenlabsVoiceId?: string;
    /** Opt-in: midnight sector reset (receptive AI, clears campaign enrollment). */
    midnightAiReactivationEnabled?: boolean;
    createdAt?: string;
    updatedAt?: string;
}

export interface CreateAiAgentSettingsDTO {
    sectorId?: number;
    isEnabled: boolean;
    prompt: string;
    knowledgeBase: string;
    autoTransferEnabled: boolean;
    humanTransferRulesEnabled: boolean;
    humanTransferRules: string;
    aiApiKey: string;
    aiModel: string;
    inactivityTimeoutMinutes?: number;
    respondWithAudio?: boolean;
    midnightAiReactivationEnabled?: boolean;
    elevenlabsVoiceId?: string;
}

export const getAiAgentSettings = async (sectorId: number, token: string): Promise<AiAgentSettingsDTO> => {
    const response = await axios.get(`${API_URL}/AiAgent/settings/${sectorId}`, {
        headers: {
            'Authorization': `Bearer ${token}`,
        },
    });
    return response.data.data;
};

export const saveAiAgentSettings = async (
    sectorId: number,
    settings: CreateAiAgentSettingsDTO,
    token: string
): Promise<AiAgentSettingsDTO> => {
    const response = await axios.post(`${API_URL}/AiAgent/settings/${sectorId}`, settings, {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
    });
    return response.data.data;
};

export const deleteAiAgentSettings = async (sectorId: number, token: string): Promise<void> => {
    await axios.delete(`${API_URL}/AiAgent/settings/${sectorId}`, {
        headers: {
            'Authorization': `Bearer ${token}`,
        },
    });
};

/** Clears broadcast-only ai_active_type on contacts (organic broadcast mode) so they follow receptive settings. */
export const disableBroadcastAIForSector = async (
    sectorId: number,
    token: string
): Promise<{ sectorId: number; contactsReset: number }> => {
    const response = await axios.post(
        `${config.UNOFFICIAL_API_URL}/sectors/${sectorId}/ai-broadcast-disable`,
        {},
        {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        }
    );
    return response.data?.data ?? { sectorId, contactsReset: 0 };
};

// Toggle AI active status for a contact
export const toggleContactAI = async (
    sectorId: number,
    contactId: number,
    aiActive: boolean,
    token: string
): Promise<{ contactId: number; aiActive: boolean }> => {
    const response = await axios.put(
        `${config.UNOFFICIAL_API_URL}/sectors/${sectorId}/contacts/${contactId}/ai-toggle`,
        { aiActive },
        {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        }
    );
    return response.data.data;
};

// Get AI status for a contact
export const getContactAIStatus = async (
    contactId: number,
    token: string
): Promise<{ contactId: number; aiActive: boolean }> => {
    const response = await axios.get(
        `${config.UNOFFICIAL_API_URL}/contacts/${contactId}/ai-status`,
        {
            headers: {
                'Authorization': `Bearer ${token}`,
            },
        }
    );
    return response.data.data;
};
